import { z } from "zod";
import { paginationQuery, searchParam } from "./pagination";

/**
 * `/affiliated-organizations` — the sponsor / broadcaster / media house a card
 * holder is affiliated with ("Press / Affiliated Organization" on the old
 * registration form). Distinct from responsible organizations: this is who the
 * person works for, not who owns the registration.
 */

export const AFFILIATED_ORGANIZATION_TYPES = ["SPONSOR", "BROADCASTER", "MEDIA_HOUSE", "OTHER"] as const;
export type AffiliatedOrganizationType = (typeof AFFILIATED_ORGANIZATION_TYPES)[number];

export const AFFILIATED_ORGANIZATION_SORT_KEYS = ["name", "type", "created_at"] as const;
export type AffiliatedOrganizationSortKey = (typeof AFFILIATED_ORGANIZATION_SORT_KEYS)[number];

export const listAffiliatedOrganizationsQuery = paginationQuery.extend({
	search: searchParam.optional(),
	type: z.enum(AFFILIATED_ORGANIZATION_TYPES).optional(),
	sort: z.enum(AFFILIATED_ORGANIZATION_SORT_KEYS).optional(),
	dir: z.enum(["asc", "desc"]).optional(),
});
export type ListAffiliatedOrganizationsQuery = z.infer<typeof listAffiliatedOrganizationsQuery>;

/** One row of `GET /affiliated-organizations`. */
export interface AffiliatedOrganizationListItem {
	affiliated_organization_id: string;
	name: string;
	type: string | null;
	/** Cards naming this affiliation. An org in use cannot be deleted. */
	cards_count: number;
	created_at: string;
	updated_at: string;
}

/** One bucket of `GET /affiliated-organizations/stats`. `type: null` = organisations with no type. */
export interface AffiliatedOrganizationTypeCount {
	type: AffiliatedOrganizationType | null;
	count: number;
}

/** `GET /affiliated-organizations/stats` — one statement, unfiltered. */
export interface AffiliatedOrganizationStats {
	total: number;
	/** Every type in `AFFILIATED_ORGANIZATION_TYPES` order (zero counts included), then the no-type bucket. */
	by_type: AffiliatedOrganizationTypeCount[];
}

/** `GET /affiliated-organizations/options` — the whole list, for `<select>` elements. */
export interface AffiliatedOrganizationOption {
	affiliated_organization_id: string;
	name: string;
	type: string | null;
}

// ── Write requests ──────────────────────────────────────────────────────────

export interface AffiliatedOrganizationFormMessages {
	nameRequired: string;
}

export const enAffiliatedOrganizationFormMessages: AffiliatedOrganizationFormMessages = {
	nameRequired: "Enter a name",
};

const affiliatedOrganizationFields = (m: AffiliatedOrganizationFormMessages) => ({
	name: z.string().trim().min(1, m.nameRequired).max(200),
	type: z.enum(AFFILIATED_ORGANIZATION_TYPES).or(z.literal("")),
});

export function makeAffiliatedOrganizationForm(m: AffiliatedOrganizationFormMessages = enAffiliatedOrganizationFormMessages) {
	return z.object(affiliatedOrganizationFields(m));
}
export const affiliatedOrganizationForm = makeAffiliatedOrganizationForm();
export type AffiliatedOrganizationForm = z.infer<typeof affiliatedOrganizationForm>;

export interface CreateAffiliatedOrganizationBody {
	name: string;
	type?: string;
}

/** `null` clears a field; omitted leaves it alone. */
export interface UpdateAffiliatedOrganizationBody {
	name?: string;
	type?: string | null;
	/** Headline for this change in the Activity feed. */
	reason?: string;
}

export const toCreateAffiliatedOrganizationBody = (f: AffiliatedOrganizationForm): CreateAffiliatedOrganizationBody => ({
	name: f.name,
	...(f.type ? { type: f.type } : {}),
});

/** Only what actually changed, so the audit row's `changed_fields` names the edit. */
export function toUpdateAffiliatedOrganizationBody(f: AffiliatedOrganizationForm, before: AffiliatedOrganizationListItem): UpdateAffiliatedOrganizationBody {
	const body: UpdateAffiliatedOrganizationBody = {};
	if (f.name !== before.name) body.name = f.name;
	const diffNullable = (next: string, prev: string | null) => (next === "" ? (prev === null ? undefined : null) : next === prev ? undefined : next);
	const type = diffNullable(f.type, before.type);
	if (type !== undefined) body.type = type;
	return body;
}
